import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  TENANT_DAILY_WORK_HOURS_MAX_LENGTH,
  TENANT_DISPLAY_NAME_MAX_LENGTH,
  TENANT_EMAIL_DOMAIN_WHITELIST_MAX_LENGTH,
  TENANT_MONTHLY_WORK_DAYS_MAX_LENGTH,
  TENANT_OFFICIAL_NAME_MAX_LENGTH,
} from '../../../../domain/entity/TenantEntity'
import { BasicInformatioTableDef } from '../../../containers/SingleSheetV2/table/model'
import { SelectOption } from '../../../model/selectOption'

// MIMEタイプではなく拡張子で指定する（FileUploadDialog が mime.getType で変換するため）。
const ICON_FILE_FORMAT = '.gif,.png,.jpg,.jpeg'

const TENANT_BASIC_INFORMATION_HEADER_ID = 'basicInformation'

// 選択肢はカスタム列挙の取得結果で、テーブル定義の関心事ではないため呼び出し側から渡す。
export type TenantCellOptions = {
  timezoneOptions: SelectOption[]
  financialYearEndMonthOptions: SelectOption[]
}

// テナント設定画面の基本情報テーブル。画面が1つしか無いため、セル定義と行構成を
// 分けずにここへまとめている。項目・並び・必須有無・桁数は機能プロパティ
// （function_properties）の tenant.* の定義に合わせている。
//
// テナント別のプロパティ上書き（function_property_configurations）は反映されない。
// 項目の必須有無・編集可否・ラベル・並びをここにハードコードしているため、同テーブルで
// hidden_if_u / required_if / editable_if_u やラベル・並びを変更しても画面に出ない。
// 最大文字数・許可文字は同テーブルの上書き対象外のため影響しない。
export const useTenantBasicInformationTableDef = ({
  timezoneOptions,
  financialYearEndMonthOptions,
}: TenantCellOptions): BasicInformatioTableDef => {
  const intl = useIntl()
  return useMemo<BasicInformatioTableDef>(() => {
    const label = (id: string) => intl.formatMessage({ id })
    return {
      tables: [
        {
          header: {
            externalId: TENANT_BASIC_INFORMATION_HEADER_ID,
            label: label('tenantV2.basicInformation'),
          },
          rows: [
            [
              {
                externalId: 'tenant.alias',
                field: 'alias',
                label: label('tenantV2.alias'),
                type: 'TEXT',
                width: 'full',
                required: false,
                // 更新APIに項目が無いため表示専用。
                editable: false,
              },
            ],
            [
              {
                externalId: 'tenant.iconUrl',
                field: 'iconUrl',
                label: label('tenantV2.iconUrl'),
                type: 'ICON',
                width: 'full',
                required: false,
                editable: true,
                fileFormat: ICON_FILE_FORMAT,
              },
            ],
            [
              {
                externalId: 'tenant.displayName',
                field: 'displayName',
                label: label('tenantV2.displayName'),
                type: 'TEXT',
                width: 'full',
                required: true,
                editable: true,
                maxLength: TENANT_DISPLAY_NAME_MAX_LENGTH,
              },
            ],
            [
              {
                externalId: 'tenant.officialName',
                field: 'officialName',
                label: label('tenantV2.officialName'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: true,
                maxLength: TENANT_OFFICIAL_NAME_MAX_LENGTH,
                help: label('tenantV2.officialName.help'),
              },
            ],
            [
              {
                externalId: 'tenant.emailDomainWhitelist',
                field: 'emailDomainWhitelist',
                label: label('tenantV2.emailDomainWhitelist'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: true,
                placeholder: label('tenantV2.emailDomainWhitelist.placeholder'),
                maxLength: TENANT_EMAIL_DOMAIN_WHITELIST_MAX_LENGTH,
                // TEXTセルに入力欄下の説明文を出す仕組みが無いため、案内は
                // ツールチップ1つにまとめている。
                help: label('tenantV2.emailDomainWhitelist.help'),
              },
            ],
            [
              {
                externalId: 'tenant.skipEmailVerification',
                field: 'skipEmailVerification',
                label: label('tenantV2.skipEmailVerification'),
                type: 'CHECKBOX',
                width: 'full',
                required: false,
                editable: true,
                help: label('tenantV2.skipEmailVerification.help'),
              },
            ],
            [
              {
                externalId: 'tenant.defaultTimezoneId',
                field: 'defaultTimezoneId',
                label: label('tenantV2.defaultTimezoneId'),
                type: 'SELECT',
                width: 'full',
                required: true,
                editable: true,
                options: timezoneOptions,
                clearable: false,
              },
            ],
            [
              {
                externalId: 'tenant.dailyWorkHours',
                field: 'dailyWorkHours',
                label: label('tenantV2.dailyWorkHours'),
                type: 'NUMBER',
                width: 'full',
                required: true,
                editable: true,
                maxLength: TENANT_DAILY_WORK_HOURS_MAX_LENGTH,
                help: label('tenantV2.workAmount.help'),
              },
            ],
            [
              {
                externalId: 'tenant.monthlyWorkDays',
                field: 'monthlyWorkDays',
                label: label('tenantV2.monthlyWorkDays'),
                type: 'NUMBER',
                width: 'full',
                required: true,
                editable: true,
                maxLength: TENANT_MONTHLY_WORK_DAYS_MAX_LENGTH,
                help: label('tenantV2.workAmount.help'),
              },
            ],
            [
              {
                externalId: 'tenant.financialYearEndMonth',
                field: 'financialYearEndMonth',
                label: label('tenantV2.financialYearEndMonth'),
                type: 'SELECT',
                width: 'full',
                required: true,
                editable: true,
                options: financialYearEndMonthOptions,
                clearable: false,
              },
            ],
            [
              {
                externalId: 'tenant.restrictTaskActualWorkByProjectPeriod',
                field: 'restrictTaskActualWorkByProjectPeriod',
                label: label('tenantV2.restrictTaskActualWorkByProjectPeriod'),
                type: 'CHECKBOX',
                width: 'full',
                required: false,
                editable: true,
              },
            ],
          ],
        },
      ],
    }
  }, [intl, timezoneOptions, financialYearEndMonthOptions])
}
